import React, { useMemo, useEffect } from 'react';
import { compose } from 'redux';
import FiltersContentLoader from '../../components/FiltersContentLoader';
import GridHistory from '../../components/GridHistory';
import HorizontalLoader from '../../components/HorizontalLoader';
import ListSearchContentLoader from '../../components/ListSearchContentLoader';
import ENDPOINT from '../../constants/endpoints';
import {CONNECT_STATUS} from '../../constants/profile';
import REVIEW_TYPES from '../../constants/review-types';
import ROUTE from '../../constants/route';
import {updateHistory} from '../../helpers/grid-history';
import {Role} from '../../helpers/user';
import withNetworkActions from '../../hocs/withNetworkActions';
import useApi from '../../hooks/useApi';
import {useGrid} from '../../hooks/useGrid';
import {useReviewAction} from '../../hooks/useReviewAction';
import {useTokenAction} from '../../hooks/useTokenAction';
import PageLayout from '../../layouts/PageLayout';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import FeedPost from '../../scenes/FeedPost';
import FeedPostForm from '../../scenes/FeedPostForm';
import List from '../../scenes/List';
import Sidebar from '../../scenes/Sidebar';
import ActionButtonsWidget from '../../scenes/Sidebar/components/ActionButtonsWidget';
import Connections from '../../scenes/Sidebar/components/Connections';
import DiscountWidget from '../../scenes/Sidebar/components/DiscountWidget';
import ProfileViewed from '../../scenes/Sidebar/components/ProfileViewed';
import RateTheFollowingDeals from '../../scenes/Sidebar/components/RateTheFollowingDeals';
import RelatedJobs from '../../scenes/Sidebar/components/RelatedJobs';
import RelatedPeople from '../../scenes/Sidebar/components/RelatedPeople';
import { action } from './action';
import './styles.css';

const Home = props => {
  const {
    user,
    history,
    match: {
      params: { filter = null },
    },
    globalActions: { updateUserResume },
    networkAction,
    pageTitle,
    globalOptions,
  } = props;

  const api = useApi();
  const isCandidateApproved = useMemo(() => Role.isCandidateApproved(user), [user]);

  useTokenAction(history);

  const {
    rate: rateReview,
    view: handleClickReviewLink,
    message: handleClickSendMessage,
    isAllowedRate: handleBeforeReviewRatingUpdate,
  } = useReviewAction(user, globalOptions);

  const grid = useGrid(ENDPOINT.COLLECTION.FEED, params => {
    updateHistory(history, params);
  });

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const message = params.get('message');

    if (message) {
      history.replace(history.location.pathname);
      action.popup.show(message);
    }
  }, [history]);

  const isCandidate = useMemo(() => Role.isCandidate(user), [user]);

  const checkPermissions = () => {
    return Role.isCandidate(user, true);
  };

  const sendPost = () => !filter && grid.actions.fetch();

  const removePost = id => action.feed.post.remove(api, id, { grid });

  const setLike = post => action.feed.like(api, user, post, { grid });

  const showPreviousComments = post =>
    action.feed.comments.showPrevious(api, post, { grid });

  const handleAddComment = comment => action.feed.comments.add(comment, grid);

  const handleRemoveComment = params =>
    action.feed.comments.remove(params, grid);

  const apply = process =>
    action.job.apply(api, history, user, { job: process }, { grid });

  const checkApply = process =>
    action.job.checkApply(
      api,
      history,
      user,
      { job: process },
      { updateUserResume }
    );

  const sendMessage = post => {
    if (post.ReviewType) {
      const review = {
        CandidateID: post.CandidateReviewID || post.CandidateID,
        AllowView: post.AllowView,
        ReviewType: post.ReviewType,
        TypeName: post.FirmTypeName,
      };

      if (post.ReviewType === REVIEW_TYPES.OVERVIEW) {
        review.OverviewID = post.OverviewID;
      }
      if (post.ReviewType === REVIEW_TYPES.INTERVIEW) {
        review.InterviewID = post.InterviewID;
      }
      if (post.ReviewType === REVIEW_TYPES.EMPLOYEE) {
        review.ReviewID = post.ReviewID;
      }

      handleClickSendMessage(review);
    } else {
      action.sendMessage(api, history, user, { post });
    }
  };

  const handleReviewRatingUpdate = (review, value) =>
    rateReview(review, value).then(() => grid.actions.fetch());

  const handleBeforeFirmRatingUpdate = () =>
    action.firm.beforeSetRating(api, user);

  const handleFirmRatingUpdate = (id, mark) =>
    action.firm.setRating(api, user, { id, mark }, { grid });

  const handleClickNewsLink = () => {
    history.push(`${ROUTE.HOME}/news`);
  };

  const handleChangedHeroStatus = id => {
    grid.actions.setDataRows(
      grid.data.rows.map(row =>
        row.CandidateID === id &&
        [1, 2, 3, 4, 5, 6, 7, 8, 19, 22, 24, 25].indexOf(Number(row.TypeID)) !==
          -1
          ? { ...row, HeroStatus: CONNECT_STATUS.REQUIRE_APPROVE }
          : row
      )
    );
  };

  const handleClickFirmPortfolioDeal = (id, emId, mark) => {
    api.post(
      ENDPOINT.ACTION.FIRM.POST_PORTFOLIO_DEAL_MARK,
      {
        id,
        emId,
        mark,
      },
      {
        handleError: response => {
          ResponseErrorDialog({ response });
          grid.actions.fetch();
        },
        handleSuccess: () => grid.actions.fetch(),
      }
    );
  };

  const handleChangeRateTheFollowingDeals = id => {
    if (grid.data.rows.some(r => r.PortfolioID && r.PortfolioID === id)) {
      grid.actions.fetch();
    }
  };

  return (
    <GridHistory history={history} grid={grid} matchParams={{ filter }}>
      <PageLayout
        pageClass="home-page"
        pageTitle={pageTitle}
        mainContent={
          <div className="home-page__feed">
            {grid.data.init ? (
              <FeedPostForm user={user} onSubmitFormHandler={sendPost} />
            ) : (
              <ListSearchContentLoader />
            )}

            <div className="home-page__feed--loader">
              {grid.data.isFetching && <HorizontalLoader />}
            </div>

            <List
              items={grid.data.rows}
              itemRender={item => (
                <FeedPost
                  key={item.NewsID}
                  user={user}
                  post={item}
                  actions={{
                    apply: apply,
                    setLike: setLike,
                    removePost: removePost,
                    onRemoveComment: handleRemoveComment,
                    checkApply: checkApply,
                    onAddComment: handleAddComment,
                    sendMessage: sendMessage,
                    showPreviousComments: showPreviousComments,
                    handleChangedHeroStatus,
                    handleClickReviewLink: handleClickReviewLink,
                    handleBeforeReviewRatingUpdate: handleBeforeReviewRatingUpdate,
                    handleReviewRatingUpdate: handleReviewRatingUpdate,
                    handleBeforeFirmRatingUpdate: handleBeforeFirmRatingUpdate,
                    handleFirmRatingUpdate: handleFirmRatingUpdate,
                    handleClickNewsLink: handleClickNewsLink,
                    checkPermissions: checkPermissions,
                    handleClickFirmPortfolioDeal,
                  }}
                />
              )}
              actions={{
                onChangePage: grid.actions.readPage,
              }}
              pagination={{
                page: grid.data.page,
                count: grid.data.rowsCount,
                perPage: grid.data.perPage,
              }}
            />
          </div>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              <ActionButtonsWidget user={user} history={history} />
              {isCandidateApproved && (
                <DiscountWidget />
              )}
              <RateTheFollowingDeals
                onSetMark={handleChangeRateTheFollowingDeals}
              />
              {isCandidate && (
                <>
                  <ProfileViewed isOwnProfile history={history} user={user} />
                  <RelatedPeople
                    user={user}
                    actions={{ connect: networkAction.connect }}
                  />
                  <Connections user={user} />
                  <RelatedJobs user={user} history={history} />
                </>
              )}
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistory>
  );
};

export default compose(
  withNetworkActions,
)(Home);
